<template>
  <vddl-draggable
    :draggable="item"
    :index="index"
    :wrapper="list"
    :dragstart="onDragStart"
    :dragend="onDragEnd"
    :moved="onMove"
    :canceled="onCancel"
    :type="type"
  >
    <div
      class="element"
      :class="{ selected: isSelected }"
      :draggable="true"
      @click="onClick"
      @contextmenu.prevent="onContextMenu($event)"
      @dragstart="onElementStartDrag($event)"
    >
      <div class="icon">
        <svg-icon
          ref="icon"
          :width="'14'"
          :height="'14'"
          fill="#bcbcbc"
          icon="app-icons/element"
          @on-load="onIconLoad"
        />
      </div>
      <div v-if="name" class="name">{{ name }}</div>
    </div>
  </vddl-draggable>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, DRAG_TYPE, DRAG_LIST } from '@/constants.js';
import SvgIcon from '../all/SvgIcon.vue';
import { fittingString } from '../../canvas-utils.js';
import { getTextContent, loadImage, truncateString } from '../../utils.js';

export default {
  name: 'List',
  components: {
    SvgIcon,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    list: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    content: {
      type: String,
      default: null,
    },
    containerId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      dragCanvas: null,
      isdisabled: false,
      isEditing: false,
      isSelected: false,
      allowedTypes: [DRAG_TYPE.BOARD, DRAG_TYPE.BOARD_CONTAINER],
    };
  },

  computed: {
    ...mapGetters('project/config/boards', ['getOpen']),
    ...mapGetters('application/drag', [
      'getItems',
      'getSource',
      'getTarget',
      'getIsEmpty',
    ]),

    name() {
      const result = getTextContent(this.title) || getTextContent(this.content);
      return truncateString(result, 100) || 'Empty element';
    },

    type() {
      return DRAG_TYPE.BOARD_ELEMENT;
    },
  },

  created() {},

  methods: {
    ...mapActions('application/drag', [
      'setItems',
      'setSource',
      'setTarget',
      'clear',
    ]),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapActions('project/data/elements', ['customDelete', 'move']),
    ...mapActions('project/config/boards', ['setOpen']),
    ...mapActions('project/data/jumpers', {
      updateJumper: 'customUpdate',
    }),

    // truncate(str, length) {
    //   if (length == null) {
    //     length = 100;
    //   }

    //   return str;
    // },

    /**
     * Custom event from svg icon load
     */
    async onIconLoad(svg) {
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      this.assetImage = await loadImage(url);
    },

    /**
     * Event handler for vddl dragstart event. The board item has started dragging.
     */
    onDragStart(draggable) {
      // Add class to body for hover fix
      document.body.classList.add('dragging');

      this.setItems({
        value: {
          type: this.type,
          ids: [this.item.id],
        },
      });
      this.setSource({
        value: {
          type: DRAG_LIST.BOARD,
          ids: [this.containerId],
          indexes: [this.index],
        },
      });
    },

    /**
     * Event handler for vddl dragend event.
     */
    onDragEnd(event) {
      // Remove class to body for hover fix
      document.body.classList.remove('dragging');

      // Remove any drag canvas
      $('.dragCanvasSidebar').remove();
    },

    onElementStartDrag($event) {
      if (this.canEdit) {
        const canvas = document.createElement('canvas');
        canvas.classList.add('dragCanvasSidebar');
        canvas.width = 200;
        canvas.height = 32;

        // This is required in Chrome (check styles below)
        document.body.append(canvas);
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(this.assetImage, 14, 8, 16, 16);

        // Draw text
        ctx.font = '12px "Open Sans",sans-serif';
        ctx.fillStyle = 'white';
        ctx.fillText(fittingString(ctx, this.name, 150), 40, 20);

        $event.dataTransfer.setDragImage(
          canvas,
          canvas.width / 2,
          canvas.height / 2
        );
      }
    },

    /**
     * Event handler for vddl drop event. This is triggered before vddl moved event.
     */
    onDrop(draggable) {
      this.setTarget({
        value: {
          type: DRAG_LIST.BOARD,
          id: this.item === undefined ? this.getRootContainer.id : this.item.id,
          index: draggable.index,
        },
      });
    },

    /**
     * Event handler for vddl moved event. The item has been moved to a correct position.
     */
    onMove(event) {
      const items = this.getItems;
      const source = this.getSource;
      const target = this.getTarget;
      // In case it was dropped on the boards tree
      if (target.type === DRAG_LIST.BOARD) {
        this.move({
          id: items.ids[0],
          tId: target.id,
          tIndex: target.index,
          apply: true,
        });
      }
      // In case it was dropped on a jumper
      if (target.type === DRAG_LIST.JUMPERS) {
        this.updateJumper({
          id: target.id,
          props: { elementId: items.ids[0] },
          apply: true,
        });
      }
      // Clear drag information
      this.clear();
    },

    /**
     * Event handler for vddl canceled event.
     */
    onCancel(event) {
      this.clear();
    },

    /**
     * Event handler for click event.
     */
    onClick() {
      emitter.emit(EVENT.ZOOMTONODE, {
        node: this.item,
        type: 'elements',
        boardId: this.containerId,
      });
    },

    /**
     * Event handler for contextmenu event.
     */
    onContextMenu(event) {
      if (this.canEdit) {
        this.isSelected = true;
        this.createContextMenu(event.clientX, event.clientY);
      }
    },

    /**
     * Create a context menu for this board element
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      this.createItem({
        name: 'Delete',
        icon: null,
        commands: {
          doAction: () => {
            this.customDelete({
              id: this.item.id,
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          this.isSelected = false;
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.element {
  display: flex;
  align-items: center;
  justify-content: left;
  min-width: 30px;
  min-height: 30px;
  width: 100%;
  position: relative;
  padding: 0 10px 0 19px;
  margin: 2px 0 0;
  cursor: pointer;
  &:hover:after,
  &.selected:after {
    content: '';
    position: absolute;
    background: #3e4748;
    opacity: 0.5;
    top: 0;
    bottom: 0;
    right: 0;
    width: 380px;
    z-index: -1;
  }
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    // width: 22px;
    // height: 22px;
    min-width: 16px;
    min-height: 16px;
    margin: 0 9px 0 0;
  }
  .name {
    display: block;
    color: #bcbcbc;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    text-align: left;
    font-size: 12px;
  }
}
</style>
